import CheckPermission from "../../../hooks/CheckPermission";
import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import {
  FaUpload,
  FaFile,
  FaImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileVideo,
  FaFileAudio,
  FaDownload,
  FaTrash,
} from "react-icons/fa";
import CustomButton from "../../../shared/CustomButton";
import { useTranslation } from "react-i18next";
const FilesTab = ({ patient }) => {
  const { hasPermission } = CheckPermission();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/"))
      return <FaImage className="text-blue-500" />;
    if (fileType === "application/pdf")
      return <FaFilePdf className="text-red-500" />;
    if (fileType.includes("word"))
      return <FaFileWord className="text-blue-600" />;
    if (fileType.includes("excel") || fileType.includes("spreadsheet"))
      return <FaFileExcel className="text-green-600" />;
    if (fileType.startsWith("video/"))
      return <FaFileVideo className="text-purple-500" />;
    if (fileType.startsWith("audio/"))
      return <FaFileAudio className="text-orange-500" />;
    return <FaFile className="text-gray-500" />;
  };

  const isImageFile = (fileType) => {
    return fileType && fileType.startsWith("image/");
  };

  const handleFileUpload = (event) => {
    if (!hasPermission('emr', 'create')) return;
    const uploadedFiles = Array.from(event.target.files);

    uploadedFiles.forEach((file) => {
      const newFile = {
        id: files.length + Math.random(),
        name: file.name,
        type: file.type,
        size: (file.size / (1024 * 1024)).toFixed(2) + " MB",
        uploadDate: new Date().toISOString().split("T")[0],
        url: isImageFile(file.type) ? URL.createObjectURL(file) : null,
        category: isImageFile(file.type) ? "Medical Images" : "Other",
        file: file, // Store the actual file object
      };

      setFiles((prev) => [newFile, ...prev]);
    });

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
  };

  const handleDeleteFile = (fileId) => {
    if (!hasPermission('emr', 'delete')) return;
    const file = files.find((f) => f.id === fileId);
    if (
      file &&
      window.confirm(`Are you sure you want to delete "${file.name}"?`)
    ) {
      setFiles(files.filter((f) => f.id !== fileId));
      if (selectedFile && selectedFile.id === fileId) {
        setSelectedFile(null);
      }
      toast.success("File deleted successfully");
    }
  };

  const handleDownloadFile = (file) => {
    if (file.url) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("File download started");
    } else {
      toast.error("File download not available for this file type");
    }
  };

  const renderFilePreview = (file) => {
    if (!file) return null;

    if (isImageFile(file.type) && file.url) {
      return (
        <div className="space-y-4 h-full flex flex-col">
          {/* Image Container */}
          <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 relative">
            <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
              <img
                src={file.url}
                alt={file.name}
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      );
    } else if (file.type === "application/pdf") {
      return (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">PDF Preview</h4>
          <div className="border border-gray-200 rounded-lg p-8 bg-gray-50 text-center">
            <FaFilePdf size={64} className="mx-auto text-red-500 mb-4" />
            <p className="text-gray-600">
              {t("PDF files cannot be previewed in browser")}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {t("Use the download button to view the file")}
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">File Preview</h4>
          <div className="border border-gray-200 rounded-lg p-8 bg-gray-50 text-center">
            {getFileIcon(file.type)}
            <div className="mt-4">
              <p className="text-gray-600 text-lg">{file.name}</p>
              <p className="text-sm text-gray-500 mt-2">
                Preview not available for this file type
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 pb-4 flex-shrink-0">
        <h2 className="text-2xl font-bold">{t("Patient Files")}</h2>
      </div>

      <div className="flex-1 px-6 pb-6 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[calc(100vh-150px)]">
          {/* Left Side - Files List */}
          <div className="lg:col-span-1 bg-gray-50 rounded-lg flex flex-col">
            <div className="p-4 pb-3 flex-shrink-0 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{t("Files")}</h3>
                <CustomButton
                  onClick={() => fileInputRef.current?.click()}
                  label={t("Upload")}
                  icon={<FaUpload size={14} />}
                  buttonType="primary"
                  className="!h-[35px]"
                />
              </div>
            </div>

            {/* Files List */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    onClick={() => handleFileSelect(file)}
                    className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                      selectedFile?.id === file.id
                        ? "bg-blue-50 border-blue-200"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <div className="mt-1">{getFileIcon(file.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 truncate">
                            {file.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {file.size} • {file.uploadDate}
                          </div>
                          <div className="text-xs text-blue-600 bg-blue-100 inline-block px-2 py-1 rounded mt-1">
                            {file.category}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(file);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-500"
                          title="Download"
                        >
                          <FaDownload size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile(file.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500"
                          title="Delete"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {files.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <FaFile size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>{t("No files found")}</p>
                    <p className="text-sm mt-1">{t("Upload files to get started")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - File Preview */}
          <div className="lg:col-span-2 bg-white rounded-lg flex flex-col border border-gray-200">
            <div className="p-4 pb-3 flex-shrink-0 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedFile ? t("File Preview") : t("Select a File")}
                </h3>
                {selectedFile && (
                  <div className="flex items-center space-x-2">
                    <CustomButton
                      onClick={() => handleDownloadFile(selectedFile)}
                      label="Download"
                      icon={<FaDownload size={14} />}
                      buttonType="blue"
                      className="!h-[35px]"
                    />
                  </div>
                )}
              </div>

              {selectedFile && (
                <div className="mt-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedFile.name}</span> •
                  <span className="ml-1">{selectedFile.size}</span> •
                  <span className="ml-1">
                    Uploaded: {selectedFile.uploadDate}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {selectedFile ? (
                renderFilePreview(selectedFile)
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <FaFile size={64} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">{t("No file selected")}</p>
                    <p>{t("Choose a file from the list to preview it here")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
};

export default FilesTab;
